import { useState, useEffect, useRef } from 'react'
import {
  AdasBox,
  AdasTypography,
  AdasCard,
  AdasRadio,
  AdasGrid,
  AdasPaper,
  AdasFormControl,
  AdasInputLabel,
  AdasSelect,
  AdasAlert
} from '@components/wrapper-components'
import { getReferralList } from 'api/api'
import { useCenterStore } from '@caradasstore/CenterStore'
import { Logger } from '../../../logger'
import { useLocation, useHistory } from 'react-router-dom'

export const ProductProfileSelector = ({ onCategorySelect, selectedProfileType, shouldShowProductProfile }) => {
  const history = useHistory()
  const useQuery = () => {
    return new URLSearchParams(useLocation().search)
  }
  const query = useQuery()
  const profileFromQuery = query.get('profile')

  const [selectedProfile, setSelectedProfile] = useState(profileFromQuery || 'calibration')
  const [referralId, setReferralId] = useState('')
  const [referralSourceList, setReferralSourceList] = useState([])

  const ccId = useCenterStore((store) => store.ccId)
  const hasFetchedReferralSource = useRef(false) // ✅ useRef to track fetch state

  const fetchReferralSource = async () => {
    try {
      const referralListResponse = await getReferralList(ccId)
      if (referralListResponse?.status === 200) {
        const formattedReferralList = referralListResponse.data
          .sort((a, b) => a.company.localeCompare(b.company))
          .map((referralObj) => ({ id: referralObj.id, value: referralObj.company }))
        setReferralSourceList(formattedReferralList)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductProfileSelector/fetchReferralSource' } })
    }
  }
  useEffect(() => {
    if (selectedProfile === 'national' && !hasFetchedReferralSource.current) {
      fetchReferralSource()
      hasFetchedReferralSource.current = true
    }
  }, [selectedProfile])

  useEffect(() => {
    const effectiveProfile = selectedProfileType || profileFromQuery || 'calibration'
    setSelectedProfile(effectiveProfile)
    setReferralId('')
    hasFetchedReferralSource.current = false

    if (effectiveProfile === 'national') {
      onCategorySelect?.({ referralId: '', profile: 'national' })
    } else {
      onCategorySelect?.('')
    }
  }, [selectedProfileType, profileFromQuery])

  const handleProfileChange = (e) => {
    const profile = e.target.value
    setSelectedProfile(profile)
    setReferralId('')

    // Update URL query param
    const params = new URLSearchParams()
    params.set('profile', profile)
    history.replace({ search: params.toString() })

    onCategorySelect?.({ referralId: '', profile })
  }

  const handleChange = (e) => {
    const selectedId = e.target.value
    setReferralId(selectedId)
    onCategorySelect?.({ referralId: selectedId, profile: selectedProfile })
  }

  return (
    <AdasBox>
      <AdasTypography variant='subtitle2' fontWeight='bold' gutterBottom mb={2}>
        SELECT PROFILE
      </AdasTypography>

      <AdasGrid container spacing={2} mb={3}>
        {['calibration', 'national'].map((profile) => {
          const isNational = profile === 'national'
          const isDisabled = isNational && !shouldShowProductProfile
          return (
            <AdasGrid item xs={12} sm={6} md={6} lg={4} xl={4} key={profile}>
              <AdasCard
                variant='outlined'
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: { xs: 'flex-start' },
                  borderRadius: 4,
                  p: 2,
                  height: '100%',
                  borderColor: selectedProfile === profile ? 'primary.main' : 'grey.300',
                  backgroundColor: selectedProfile === profile ? '#F2F9FF' : 'transparent',
                  gap: 2
                }}
              >
                <AdasRadio checked={selectedProfile === profile} onChange={handleProfileChange} value={profile} />
                <AdasBox>
                  <AdasTypography variant='subtitle1'>{isNational ? 'National Agreement' : 'Calibration Center'}</AdasTypography>
                  <AdasTypography variant='body2' color='text.secondary'>
                    {isNational ? 'Special pricing for Car ADAS network partners' : 'Standard pricing for calibration center services'}
                  </AdasTypography>
                </AdasBox>
              </AdasCard>
            </AdasGrid>
          )
        })}
      </AdasGrid>

      {selectedProfile === 'national' && !shouldShowProductProfile && (
        <AdasAlert
          severity='warning'
          sx={{
            my: 2,
            backgroundColor: '#FFFBE6',
            border: '1px solid #FFE58F',
            color: '#614700',
            borderRadius: '8px'
          }}
        >
          Please contact support to enroll in National Agreements
        </AdasAlert>
      )}

      {selectedProfile === 'national' && shouldShowProductProfile && (
        <AdasPaper
          elevation={0}
          sx={{
            backgroundColor: '#f5faff',
            p: 2,
            borderRadius: 2
          }}
        >
          <AdasTypography variant='subtitle2' fontWeight='bold' gutterBottom mb={2}>
            DETAILS
          </AdasTypography>

          <AdasGrid container spacing={2}>
            <AdasGrid item xs={12} sm={8}>
              <AdasFormControl fullWidth sx={{ mb: { xs: 2, sm: 3 }, ml: { xs: 0, sm: 2 } }}>
                <AdasInputLabel id='insurance-label'>Select Referral Source</AdasInputLabel>
                <AdasSelect
                  labelId='insurance-label'
                  id='referral-select'
                  name='referral_id'
                  value={referralId}
                  onChange={handleChange}
                  label='Select Referral Source'
                  options={referralSourceList}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '16px'
                    }
                  }}
                />
              </AdasFormControl>
            </AdasGrid>
          </AdasGrid>
        </AdasPaper>
      )}
    </AdasBox>
  )
}
